import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import OrderFormDialog from "@/components/OrderFormDialog";
import StatusBadge from "@/components/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const ORDER_STATUSES = ["Placed", "Processing", "Completed", "Cancelled"];

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);

  const customerMap = customers.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cRes] = await Promise.all([
        api.get("/orders", { params: { status: statusFilter } }),
        api.get("/customers"),
      ]);
      setOrders(oRes.data);
      setCustomers(cRes.data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [load]);

  const updateStatus = async (id, s) => {
    try {
      await api.put(`/orders/${id}`, { status: s });
      setOrders((arr) => arr.map((o) => (o.id === id ? { ...o, status: s } : o)));
      toast.success(`Order set to ${s}`);
    } catch {
      toast.error("Failed");
    }
  };

  const total = orders.reduce((sum, o) => sum + Number(o.amount || 0), 0);

  return (
    <div data-testid="orders-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Revenue
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Orders
            </h1>
          </div>
          <button
            onClick={() => setOpenForm(true)}
            data-testid="orders-new-button"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Plus size={16} weight="bold" /> New Order
          </button>
        </div>
      </div>

      <div className="p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Orders
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              {orders.length}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Total value
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Filter
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="mt-3 h-10 bg-white border-slate-200"
                data-testid="orders-status-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  <th className="px-6 py-3">Order</th>
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && orders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      No orders.
                    </td>
                  </tr>
                )}
                {!loading &&
                  orders.map((o) => {
                    const c = customerMap[o.customer_id];
                    return (
                      <tr
                        key={o.id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`order-row-${o.id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{o.order_code}</div>
                          <div className="text-xs text-slate-500">{o.description || "—"}</div>
                        </td>
                        <td className="px-6 py-4">
                          {c ? (
                            <button
                              onClick={() => navigate(`/sales/customers/${c.id}`)}
                              className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                              {c.name}
                            </button>
                          ) : (
                            "—"
                          )}
                          <div className="text-xs text-slate-500">{c?.company}</div>
                        </td>
                        <td className="px-6 py-4 text-slate-800 font-medium">
                          ₹{Number(o.amount).toLocaleString("en-IN")}
                        </td>
                        <td className="px-6 py-4">
                          <Select
                            value={o.status}
                            onValueChange={(s) => updateStatus(o.id, s)}
                          >
                            <SelectTrigger
                              className="w-36 h-8 text-xs bg-white"
                              data-testid={`order-status-select-${o.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ORDER_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 text-slate-500 text-xs">
                          {new Date(o.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {openForm && (
        <OrderFormDialog
          open={openForm}
          customers={customers}
          onClose={() => setOpenForm(false)}
          onSaved={() => {
            setOpenForm(false);
            load();
            toast.success("Order created");
          }}
        />
      )}
    </div>
  );
}
